import { useMemo } from 'react'
import {
  getImagesFromVariantOrPlaceholder,
  IFormattedImage,
  IVariant,
} from '@core/api/Products/types'
import { useProductDetailContext } from '@core/utils/models/product/ProductContext'
import { getProductImageSEOProps } from '@core/utils/seo/getProductImageSEOProps'
import { useTranslation } from '@core/i18n/i18n'
import config from '@core/config/config'

export const useImageSelection = (variant: IVariant | undefined) => {
  const images = useMemo(
    () => (variant ? getImagesFromVariantOrPlaceholder(variant) : []),
    [variant]
  )
  return { images }
}

export const getLargeImageSrc = (imageSrc: string): string => {
  return imageSrc.replace(
    config.features.largeImageCalculation.smallImageSearchRegex,
    config.features.largeImageCalculation.largeImageSrcReplace
  )
}

export const useFormattedImages = () => {
  const { product, variant } = useProductDetailContext() || {}
  const imageSelection = useImageSelection(variant)
  const productName = product?.name
  const { t } = useTranslation()
  return useMemo(
    () =>
      imageSelection.images.map((image, index) => {
        const { alt, title } = getProductImageSEOProps(productName!, index + 1)
        const imageData = {
          original: image.url,
          thumbnail: image.url,
          fullscreen: image.url,
          alt,
          title,
          originalAlt: alt,
          originalTitle: title,
          thumbnailAlt: `${t(`preview`)}: ${alt}`,
          thumbnailTitle: `${t(`preview`)}: ${title}`,
          thumbnailWidth: 100,
          thumbnailHeight: 100,
        }

        if (config.features.fullSizeImageGallery.enabled) {
          imageData.fullscreen = getLargeImageSrc(image.url)
        }
        return imageData
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageSelection, productName]
  )
}

export const useFormattedImagesWithDescription = (
  formattedImages: IFormattedImage[]
) => {
  return useMemo(
    () =>
      formattedImages.map((item: IFormattedImage) => {
        return { ...item, description: item.title }
      }),
    [formattedImages]
  )
}
